import React, { useMemo, useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, BarChart2 } from 'lucide-react';
import { format, subDays, startOfDay } from 'date-fns';

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload?.length) {
    return (
      <div className="bg-[#0a1628] border border-[#ffd700]/30 rounded-xl p-3 shadow-xl">
        <p className="text-white/60 text-xs mb-1">{label}</p>
        {payload?.map((entry, i) => (
          <p key={i} className="text-[#ffd700] font-semibold text-sm">
            {currency || 'KES'} {new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(entry?.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const EarningsChart = ({ transactions, currency }) => {
  const [view, setView] = React.useState('daily');

  const dailyData = useMemo(() => {
    const days = 7;
    const data = Array.from({ length: days }, (_, i) => {
      const date = subDays(new Date(), days - 1 - i);
      const dayStart = startOfDay(date);
      const dayEnd = new Date(dayStart.getTime() + 86400000);
      const dayLabel = format(date, 'EEE');

      const credits = (transactions || [])?.filter(tx => {
          const txDate = new Date(tx?.created_at);
          return tx?.ledger_type === 'credit' && tx?.status === 'completed' && txDate >= dayStart && txDate < dayEnd;
        })?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);

      const debits = (transactions || [])?.filter(tx => {
          const txDate = new Date(tx?.created_at);
          return tx?.ledger_type === 'debit' && tx?.status === 'completed' && txDate >= dayStart && txDate < dayEnd;
        })?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);

      return { day: dayLabel, earnings: credits, withdrawals: debits, net: credits - debits };
    });
    return data;
  }, [transactions]);

  const weeklyData = useMemo(() => {
    const weeks = 4;
    return Array.from({ length: weeks }, (_, i) => {
      const weekStart = subDays(new Date(), (weeks - i) * 7);
      const weekEnd = subDays(new Date(), (weeks - i - 1) * 7);
      const label = `W${i + 1}`;

      const earnings = (transactions || [])?.filter(tx => {
          const txDate = new Date(tx?.created_at);
          return tx?.ledger_type === 'credit' && tx?.status === 'completed' && txDate >= weekStart && txDate < weekEnd;
        })?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);

      return { day: label, earnings, net: earnings };
    });
  }, [transactions]);

  const chartData = view === 'daily' ? dailyData : weeklyData;
  const totalEarnings = chartData?.reduce((sum, d) => sum + d?.earnings, 0);

  return (
    <div className="bg-[#0d1f3c] rounded-2xl border border-white/10 p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#ffd700]/20 flex items-center justify-center">
            <BarChart2 className="w-4 h-4 text-[#ffd700]" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Earnings Breakdown</h3>
            <p className="text-white/40 text-xs flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-emerald-400" />
              {currency || 'KES'} {new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(totalEarnings)} total
            </p>
          </div>
        </div>
        <div className="flex bg-white/5 rounded-lg p-0.5">
          {['daily', 'weekly']?.map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors capitalize ${
                view === v ? 'bg-[#ffd700] text-[#0a1628]' : 'text-white/50 hover:text-white/70'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ffd700" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ffd700" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip currency={currency} />} />
            <Area type="monotone" dataKey="earnings" stroke="#ffd700" strokeWidth={2} fill="url(#earningsGradient)" dot={{ fill: '#ffd700', r: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-3">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#ffd700]" />
          <span className="text-white/40 text-xs">Earnings (credits)</span>
        </div>
      </div>
    </div>
  );
};

export default EarningsChart;
